import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { BackLink } from 'compositions/BackLink'
import { ConfirmDialog } from 'compositions/ConfirmDialog'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { Page } from 'compositions/Page'
import type { AssociationKind } from 'hooks/api'
import {
  useAddContactAssociation,
  useContact,
  useDeleteContact,
  useRemoveContactAssociation,
  useUpdateContact,
  useUpdateContactAssociation,
} from 'hooks/api'
import { getApiErrorMessage } from 'hooks/useFetchApi'
import { Pencil, Save, Trash2, X, XCircle } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import type { ContactDetail as ContactDetailType } from 'types/contacts'
import { formatPhone } from 'utils/phone'

import { ContactAssociations } from './contact-associations'
import { ContactCommunicationHistory } from './contact-communication-history'
import { type ContactFormData, ContactFormFields } from './contact-form-fields'
import { ContactSubscriptions } from './contact-subscriptions'
import { MergeDialog } from './merge-dialog'
function contactToFormData(contact: ContactDetailType): ContactFormData {
  return {
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    email: contact.email ?? '',
    phoneNumber: contact.phoneNumber ?? '',
    organizationName: contact.organizationName ?? '',
    ordoUserId: contact.ordoUserId ?? '',
    timezone: contact.timezone ?? '',
    notes: contact.notes ?? '',
  }
}

/**
 * Stringify an extraData value for display. Objects and arrays go through
 * JSON.stringify so they don't render as the literal "[object Object]".
 */
function formatExtraDataValue(value: unknown): string {
  if (value == null) {
    return ''
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="space-y-0.5 min-w-0">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm wrap-break-word">{value || '-'}</dd>
    </div>
  )
}

/**
 * Extra-data values that aren't scalar — arrays or nested objects — render
 * full-width in a pre-formatted block with its own scroll so long JSON
 * doesn't break the page layout.
 */
function ComplexExtraDataRow({ label, value }: { label: string; value: unknown }) {
  let pretty: string
  try {
    pretty = JSON.stringify(value, null, 2)
  } catch {
    pretty = String(value)
  }
  return (
    <div className="space-y-1 min-w-0">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd>
        <pre className="max-h-48 overflow-auto rounded-md bg-muted p-2 text-xs font-mono whitespace-pre-wrap break-all">
          {pretty}
        </pre>
      </dd>
    </div>
  )
}

function isComplexExtraValue(value: unknown): boolean {
  if (value == null) {
    return false
  }
  if (Array.isArray(value)) {
    // Empty arrays (and arrays of scalars) fit fine in the scalar grid;
    // only arrays containing nested objects need the full-width pre-block.
    return value.some((v) => typeof v === 'object' && v !== null)
  }
  if (typeof value === 'object') {
    // Treat empty objects as scalars too — `{}` is trivial and looks silly
    // in a dedicated pre-block.
    return Object.keys(value as Record<string, unknown>).length > 0
  }
  // Very long scalar strings also need the complex treatment so they don't
  // blow out the grid. 120 chars is roughly the point where an InfoRow wraps
  // weirdly in the 2-col layout.
  if (typeof value === 'string' && value.length > 120) {
    return true
  }
  return false
}

function ContactDetailContent({ contact, refetch }: { contact: ContactDetailType | null; refetch: () => void }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { perform: updateContact, loading: saving } = useUpdateContact()
  const { perform: deleteContact } = useDeleteContact()
  const { perform: addAssociation } = useAddContactAssociation()
  const { perform: updateAssociation } = useUpdateContactAssociation()
  const { perform: removeAssociation } = useRemoveContactAssociation()

  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    organizationName: '',
    ordoUserId: '',
    timezone: '',
    notes: '',
  })
  const [editExtraData, setEditExtraData] = useState<Record<string, string>>({})

  useEffect(() => {
    if (contact) {
      setFormData(contactToFormData(contact))
    }
  }, [contact])

  const handleFieldChange = useCallback((field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const startEditing = useCallback(() => {
    if (contact) {
      setFormData(contactToFormData(contact))
      const extra: Record<string, string> = {}
      if (contact.extraData) {
        for (const [key, value] of Object.entries(contact.extraData)) {
          extra[key] = formatExtraDataValue(value)
        }
      }
      setEditExtraData(extra)
    }
    setEditing(true)
  }, [contact])

  const cancelEditing = useCallback(() => {
    if (contact) {
      setFormData(contactToFormData(contact))
    }
    setEditExtraData({})
    setEditing(false)
  }, [contact])

  const handleSave = useCallback(async () => {
    if (!id) {
      return
    }
    const payload: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(formData)) {
      payload[key] = value || undefined
    }
    const filteredExtra: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(editExtraData)) {
      if (value !== '') {
        filteredExtra[key] = value
      }
    }
    payload.extraData = filteredExtra
    try {
      await updateContact(id, payload)
      refetch()
      setEditing(false)
    } catch (err) {
      toast.error('Failed to save contact', { description: getApiErrorMessage(err) })
    }
  }, [id, formData, editExtraData, updateContact, refetch])

  const handleDelete = useCallback(async () => {
    if (!id) {
      return
    }
    try {
      await deleteContact(id)
      navigate('/contacts')
    } catch (err) {
      toast.error('Failed to delete contact', { description: getApiErrorMessage(err) })
    }
  }, [id, deleteContact, navigate])

  const handleAddAssociation = useCallback(
    async (kind: AssociationKind, externalId: string, role: string) => {
      if (!id) {
        return
      }
      await addAssociation(id, kind, externalId, role)
      refetch()
    },
    [id, addAssociation, refetch],
  )

  const handleUpdateAssociation = useCallback(
    async (kind: AssociationKind, associationId: string, role: string) => {
      if (!id) {
        return
      }
      await updateAssociation(id, kind, associationId, role)
      refetch()
    },
    [id, updateAssociation, refetch],
  )

  const handleRemoveAssociation = useCallback(
    async (kind: AssociationKind, associationId: string) => {
      if (!id) {
        return
      }
      await removeAssociation(id, kind, associationId)
      refetch()
    },
    [id, removeAssociation, refetch],
  )

  if (!contact) {
    return <div className="py-12 text-center text-muted-foreground">Contact not found</div>
  }

  const displayName = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || '(unnamed)'

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle>{displayName}</CardTitle>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {editing && (
            <>
              <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving}>
                <X className="mr-1 h-4 w-4" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                <Save className="mr-1 h-4 w-4" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
          {!editing && (
            <>
              <MergeDialog contact={contact} onComplete={refetch} />
              <Button variant="outline" size="sm" onClick={startEditing}>
                <Pencil className="mr-1 h-4 w-4" />
                Edit
              </Button>
              <ConfirmDialog
                title="Delete contact"
                description={`Are you sure you want to delete ${displayName}? This action cannot be undone.`}
                onConfirm={handleDelete}
                trigger={
                  <Button variant="destructive" size="sm">
                    <Trash2 className="mr-1 h-4 w-4" />
                    Delete
                  </Button>
                }
              />
            </>
          )}
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="pt-6">
        {editing && (
          <>
            <ContactFormFields data={formData} onChange={handleFieldChange} />
            {Object.keys(editExtraData).length > 0 && (
              <div className="mt-6 space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Extra Data</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {Object.entries(editExtraData).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <label className="w-28 shrink-0 truncate text-sm text-muted-foreground" title={key}>
                        {key}
                      </label>
                      <Input
                        value={value}
                        onChange={(e) => setEditExtraData((prev) => ({ ...prev, [key]: e.target.value }))}
                        className="min-w-0 flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        onClick={() =>
                          setEditExtraData((prev) => {
                            const next = { ...prev }
                            delete next[key]
                            return next
                          })
                        }>
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {!editing && (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 [overflow-wrap:anywhere]">
            <InfoRow label="First Name" value={contact.firstName} />
            <InfoRow label="Last Name" value={contact.lastName} />
            <InfoRow label="Email" value={contact.email} />
            <InfoRow label="Phone" value={formatPhone(contact.phoneNumber)} />
            <InfoRow label="Organization" value={contact.organizationName} />
            <InfoRow label="Ordo ID" value={contact.ordoUserId} />
            <InfoRow label="Timezone" value={contact.timezone} />
            <div className="sm:col-span-2">
              <InfoRow label="Notes" value={contact.notes} />
            </div>
            {contact.extraData &&
              Object.keys(contact.extraData).length > 0 &&
              (() => {
                // Split scalars from complex values so the scalar grid stays
                // tightly staggered; complex values (JSON blobs) render in
                // their own full-width section below.
                const entries = Object.entries(contact.extraData)
                const scalars = entries.filter(([, v]) => !isComplexExtraValue(v))
                const complex = entries.filter(([, v]) => isComplexExtraValue(v))
                return (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-muted-foreground">Extra Data</dt>
                    <dd className="mt-1 space-y-3">
                      {scalars.length > 0 && (
                        <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2 [overflow-wrap:anywhere]">
                          {scalars.map(([key, value]) => {
                            const formatted = formatExtraDataValue(value)
                            return <InfoRow key={key} label={key} value={formatted.length > 0 ? formatted : null} />
                          })}
                        </dl>
                      )}
                      {complex.length > 0 && (
                        <dl className="space-y-2">
                          {complex.map(([key, value]) => (
                            <ComplexExtraDataRow key={key} label={key} value={value} />
                          ))}
                        </dl>
                      )}
                    </dd>
                  </div>
                )
              })()}
          </dl>
        )}

        <ContactAssociations
          schoolLocations={contact.schoolLocations}
          vendorLocations={contact.vendorLocations}
          organizations={contact.organizations}
          editing={editing}
          onAdd={handleAddAssociation}
          onUpdate={handleUpdateAssociation}
          onRemove={handleRemoveAssociation}
        />

        <div className="mt-8">
          <ContactSubscriptions contactId={contact.id} />
        </div>

        <div className="mt-8">
          <ContactCommunicationHistory contactId={contact.id} />
        </div>
      </CardContent>
    </Card>
  )
}

export function ContactDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: contact, loading, refetch } = useContact(id)

  if (!loading && !contact) {
    return (
      <Page>
        <div className="py-12 text-center text-muted-foreground">Contact not found</div>
      </Page>
    )
  }

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/contacts">Back to Contacts</BackLink>
      </div>

      <LoadingWrapper
        loading={loading}
        loadingElement={
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <Separator />
            <CardContent className="space-y-4 pt-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </div>
            </CardContent>
          </Card>
        }>
        <ContactDetailContent contact={contact} refetch={refetch} />
      </LoadingWrapper>
    </Page>
  )
}
